import * as React from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useBulkUpdateIntakesMutation } from '@/services/api/intakeService';
import { IntakeListItem } from '@/services/types/intake-types';

interface BulkMarkNoticeSentActionProps {
  selectedIntakes: IntakeListItem[];
  onComplete: () => void;
}

export const BulkMarkNoticeSentAction: React.FC<
  BulkMarkNoticeSentActionProps
> = ({ selectedIntakes, onComplete }) => {
  const [isConfirmOpen, setIsConfirmOpen] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const [bulkUpdateIntakes] = useBulkUpdateIntakesMutation();

  const handleConfirm = async () => {
    setIsSubmitting(true);

    try {
      const result = await bulkUpdateIntakes({
        intake_ids: selectedIntakes.map((i) => i.id),
        updates: { rejection_email_sent: true },
      }).unwrap();

      if (result.updated > 0) {
        toast.success(
          `Marked ${result.updated} intake${result.updated !== 1 ? 's' : ''} as notice sent`,
        );
      } else {
        toast.warning('No intakes were updated');
      }
    } catch {
      toast.error('Failed to update intakes');
      setIsSubmitting(false);
      return;
    }

    setIsSubmitting(false);
    setIsConfirmOpen(false);
    onComplete();
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsConfirmOpen(true)}
        disabled={isSubmitting}
      >
        Mark Notice Sent
      </Button>

      <Dialog
        open={isConfirmOpen}
        onOpenChange={(open) => !open && !isSubmitting && setIsConfirmOpen(false)}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Mark Notice Sent</DialogTitle>
            <DialogDescription>
              Are you sure you want to mark{' '}
              <strong>{selectedIntakes.length}</strong> intake
              {selectedIntakes.length !== 1 ? 's' : ''} as rejection notice
              sent? This will remove them from the Notice Needed view.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setIsConfirmOpen(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={isSubmitting}>
              {isSubmitting
                ? 'Updating...'
                : `Mark ${selectedIntakes.length} as Sent`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
